import { useRef, useState } from 'react';
import { Plus, Trash2, Upload } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field, Input, Textarea } from '@/components/ui/Field';
import { useToast } from '@/components/ui/ToastContext';
import { uploadPageImage } from '@/lib/customPageApi';
import { shopKeysOf, useShopConfig, useShopMembers, type ShopKind } from '@/lib/shopConfig';
import { isVideo } from '@/shared/rxk-product/media';
import type { FaqItem, ProductConcern, ProductDesignV2, ProductIngredientCard, ProductRitualStep, ProductSkinTip } from '@/types/product';

// Editor for rxk_product_content.design_v2: the sections only the new-design
// product page shows (src/pages/products/v2/ProductV2.tsx). Leaving a part
// empty hides that section on the page.

function ImagePick({ value, onChange, label }: { value?: string; onChange: (url: string) => void; label: string }) {
  const { push } = useToast();
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      onChange(await uploadPageImage(file));
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Field label={label}>
      <div className="flex items-center gap-3">
        {value && <img src={value} alt="" className="h-14 w-14 object-cover bg-surface-muted rounded-lg" />}
        <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={pick} />
        <Button type="button" size="sm" variant="secondary" onClick={() => ref.current?.click()} disabled={busy}>
          <Upload className="w-3.5 h-3.5" /> {busy ? 'Uploading…' : value ? 'Replace' : 'Upload'}
        </Button>
      </div>
    </Field>
  );
}

// One item per line, blank lines dropped.
function LinesField({ label, value, onChange, rows = 3 }: { label: string; value?: string[]; onChange: (v: string[]) => void; rows?: number }) {
  return (
    <Field label={label}>
      <Textarea value={(value ?? []).join('\n')} onChange={(e) => onChange(e.target.value.split('\n').filter((l, i, a) => l.trim() || i === a.length - 1))} rows={rows} />
    </Field>
  );
}

// Round toggle for one shop circle: photo, name, ticked or not.
function CircleToggle({ on, image, label, onClick }: { on: boolean; image: string; label: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={on} className="group flex flex-col items-center gap-1.5 w-[84px] cursor-pointer">
      <span
        className={`relative w-16 h-16 rounded-full overflow-hidden bg-surface-muted transition ${on ? 'ring-[3px] ring-primary-700 ring-offset-2' : 'ring-1 ring-border-subtle opacity-60 group-hover:opacity-100'}`}
      >
        {image && <img src={image} alt="" className="w-full h-full object-cover" />}
        {on && <span className="absolute right-0.5 bottom-0.5 w-5 h-5 rounded-full bg-primary-700 text-white text-[11px] grid place-items-center">✓</span>}
      </span>
      <span className={`text-[11.5px] leading-tight text-center ${on ? 'font-semibold text-ink' : 'text-ink-faint'}`}>{label}</span>
    </button>
  );
}

export default function DesignV2Editor({
  value,
  onChange,
  stepKeyword,
  skinTypes,
}: {
  value: ProductDesignV2;
  onChange: (v: ProductDesignV2) => void;
  // For the automatic shop circles (by routine step and skin types) until they are picked here.
  stepKeyword: string;
  skinTypes: string[];
}) {
  const set = (patch: Partial<ProductDesignV2>) => onChange({ ...value, ...patch });
  const ritual = value.ritual ?? {};
  const texture = value.texture ?? {};
  const watch = value.watch ?? {};
  const concerns = value.concerns ?? [];
  const steps = ritual.steps ?? [];
  const setConcern = (i: number, patch: Partial<ProductConcern>) => set({ concerns: concerns.map((c, j) => (j === i ? { ...c, ...patch } : c)) });
  const setStep = (i: number, patch: Partial<ProductRitualStep>) => set({ ritual: { ...ritual, steps: steps.map((s, j) => (j === i ? { ...s, ...patch } : s)) } });
  const gallery = value.gallery ?? [];
  const skins = value.skins ?? [];
  const ingredients = value.ingredients ?? {};
  const ings = ingredients.items ?? [];
  const faqs = value.faqs ?? [];
  const ba = value.beforeAfter ?? {};
  const setSkin = (i: number, patch: Partial<ProductSkinTip>) => set({ skins: skins.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
  const setIng = (i: number, patch: Partial<ProductIngredientCard>) => set({ ingredients: { ...ingredients, items: ings.map((x, j) => (j === i ? { ...x, ...patch } : x)) } });
  const setFaq = (i: number, patch: Partial<FaqItem>) => set({ faqs: faqs.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
  const shop = useShopConfig();
  const story = value.story ?? {};
  const gift = value.gift ?? {};
  const results = value.results ?? [];
  const members = useShopMembers();
  const shopKeys = (kind: ShopKind) => shopKeysOf({ stepKeyword, skinTypes, designV2: value }, kind);
  const field = (kind: ShopKind) => (kind === 'categories' ? 'shopCategories' : 'shopConcerns');
  const toggleCircle = (kind: ShopKind, key: string) => {
    const cur = shopKeys(kind);
    set({ [field(kind)]: cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key] });
  };
  const del = 'w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-lg hover:bg-error-bg text-error cursor-pointer';

  return (
    <>
      <Card>
        <CardHeader title="New design — top of the page" subtitle="Shown beside the product photos" />
        <CardBody className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Title — first line">
              <Input value={value.title ?? ''} onChange={(e) => set({ title: e.target.value })} placeholder="Skin Priming" />
            </Field>
            <Field label="Title — second line (italic)">
              <Input value={value.titleEm ?? ''} onChange={(e) => set({ titleEm: e.target.value })} placeholder="Booster Essence" />
            </Field>
            <Field label="Slogan">
              <Input value={value.slogan ?? ''} onChange={(e) => set({ slogan: e.target.value })} placeholder="PREP · BRIGHTEN · BOOST" />
            </Field>
          </div>
          <Field label="Intro text (falls back to Description)">
            <Textarea value={value.lede ?? ''} onChange={(e) => set({ lede: e.target.value })} rows={3} />
          </Field>
          <Field label="Benefits (first accordion)">
            <Textarea value={value.benefits ?? ''} onChange={(e) => set({ benefits: e.target.value })} rows={3} />
          </Field>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="New design — name, photos and labels" subtitle="Blank fields use the product's normal content (as on the classic page)." />
        <CardBody className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Display name (breadcrumb, buy bar, 3-step cards)">
              <Input value={value.name ?? ''} onChange={(e) => set({ name: e.target.value })} placeholder="Skin Priming Booster Essence" />
            </Field>
            <Field label="Short name (video thumbnails)">
              <Input value={value.short ?? ''} onChange={(e) => set({ short: e.target.value })} placeholder="Booster" />
            </Field>
            <Field label='Name in "Why use the …?"'>
              <Input value={value.whyName ?? ''} onChange={(e) => set({ whyName: e.target.value })} placeholder="Booster Essence" />
            </Field>
          </div>
          <div className="space-y-2">
            <p className="text-xs font-medium text-text-secondary">Gallery (in order). For a video, paste a YouTube link (or an .mp4 link) and, if you like, a poster image.</p>
            {gallery.map((g, i) => (
              <div key={i} className="flex items-center gap-2">
                {g.src && !isVideo(g.src) && <img src={g.src} alt="" className="h-10 w-10 object-cover rounded-md bg-surface-muted" />}
                <Input
                  value={g.src}
                  onChange={(e) => set({ gallery: gallery.map((x, j) => (j === i ? { ...x, src: e.target.value } : x)) })}
                  placeholder="Image, YouTube or .mp4 link"
                />
                {isVideo(g.src) && (
                  <Input
                    value={g.poster ?? ''}
                    onChange={(e) => set({ gallery: gallery.map((x, j) => (j === i ? { ...x, poster: e.target.value } : x)) })}
                    placeholder="Poster image link"
                  />
                )}
                <button type="button" className={del} aria-label="Remove" onClick={() => set({ gallery: gallery.filter((_, j) => j !== i) })}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            <div className="flex gap-2">
              <ImagePick label="" onChange={(src) => set({ gallery: [...gallery, { src }] })} />
              <Button type="button" size="sm" variant="secondary" onClick={() => set({ gallery: [...gallery, { src: '' }] })}>
                <Plus className="w-3.5 h-3.5" /> Add link
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <LinesField label="Claim boxes under the price (one per line, up to 5)" value={value.claims} onChange={(l) => set({ claims: l })} />
            <LinesField label="Moving ribbon words (one per line)" value={value.marquee} onChange={(l) => set({ marquee: l })} />
            <LinesField label="How to use (one step per line)" value={value.howTo} onChange={(l) => set({ howTo: l })} rows={4} />
            <Field label="Full ingredients (INCI)">
              <Textarea value={value.inci ?? ''} onChange={(e) => set({ inci: e.target.value })} rows={4} />
            </Field>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="New design — Home page" subtitle="The line under this product in the new Home's daily-ritual list." />
        <CardBody className="space-y-3">
          <Field label="Line in the daily-ritual list">
            <Input value={value.homeLine ?? ''} onChange={(e) => set({ homeLine: e.target.value })} placeholder="Hydrates and preps skin right after cleansing." />
          </Field>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="New design — Shop circles" subtitle="Tap the circles this product should appear under on the Home shop and in the header menu." />
        <CardBody className="space-y-5">
          {(
            [
              ['categories', 'Shop by category'],
              ['concerns', 'Shop by concern'],
            ] as const
          ).map(([kind, title]) => (
            <div key={kind}>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider">{title}</p>
                {value[field(kind)] ? (
                  <button type="button" className="text-[11.5px] text-ink-faint hover:text-ink cursor-pointer" onClick={() => set({ [field(kind)]: undefined })}>
                    Back to automatic
                  </button>
                ) : (
                  <span className="text-[11.5px] text-ink-faint">Automatic ({kind === 'categories' ? 'by routine step' : 'by skin types'}) until you change it</span>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {shop[kind].map((c) => (
                  <CircleToggle
                    key={c.key}
                    on={shopKeys(kind).includes(c.key)}
                    image={c.image || members.find((m) => m[kind].includes(c.key))?.image || ''}
                    label={(c.label.en || c.key) + (c.hidden ? ' (hidden)' : '')}
                    onClick={() => toggleCircle(kind, c.key)}
                  />
                ))}
              </div>
            </div>
          ))}
          <p className="text-xs text-text-tertiary">To add, rename or re-photo a circle: Website → Home Page → Shop categories &amp; concerns.</p>
        </CardBody>
      </Card>

      {stepKeyword === 'SET' && (
        <Card>
          <CardHeader title="New design — 3-Step page sections" subtitle="The scroll story, the gift box and the results timeline. Blank fields use the default text." />
          <CardBody className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Scroll story — small label">
                <Input value={story.kicker ?? ''} onChange={(e) => set({ story: { ...story, kicker: e.target.value } })} placeholder="The five-minute ritual" />
              </Field>
              <Field label="Scroll story — heading, first line">
                <Input value={story.title ?? ''} onChange={(e) => set({ story: { ...story, title: e.target.value } })} placeholder="Three steps," />
              </Field>
              <Field label="Scroll story — heading, second line">
                <Input value={story.titleEm ?? ''} onChange={(e) => set({ story: { ...story, titleEm: e.target.value } })} placeholder="one routine." />
              </Field>
            </div>
            <Field label="Scroll story — intro">
              <Textarea value={story.lead ?? ''} onChange={(e) => set({ story: { ...story, lead: e.target.value } })} rows={2} />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[0, 1, 2].map((i) => (
                <Field key={i} label={`Step 0${i + 1} text (blank = the product's Home line)`}>
                  <Textarea
                    value={story.steps?.[i]?.text ?? ''}
                    onChange={(e) => {
                      const steps2 = [0, 1, 2].map((k) => ({ text: k === i ? e.target.value : (story.steps?.[k]?.text ?? '') }));
                      set({ story: { ...story, steps: steps2 } });
                    }}
                    rows={3}
                  />
                </Field>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 pt-2 border-t border-border-subtle">
              <ImagePick label="Gift box photo (shown when the lid opens)" value={gift.image} onChange={(image) => set({ gift: { ...gift, image } })} />
              <Field label="Gift box — small label">
                <Input value={gift.kicker ?? ''} onChange={(e) => set({ gift: { ...gift, kicker: e.target.value } })} placeholder="The gift box" />
              </Field>
              <Field label="Gift box — heading, first line">
                <Input value={gift.title ?? ''} onChange={(e) => set({ gift: { ...gift, title: e.target.value } })} placeholder="Made to" />
              </Field>
              <Field label="Gift box — heading, second line">
                <Input value={gift.titleEm ?? ''} onChange={(e) => set({ gift: { ...gift, titleEm: e.target.value } })} placeholder="be given." />
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Gift box — text">
                <Textarea value={gift.text ?? ''} onChange={(e) => set({ gift: { ...gift, text: e.target.value } })} rows={3} />
              </Field>
              <LinesField label="Gift box — occasions (one per line)" value={gift.occasions} onChange={(l) => set({ gift: { ...gift, occasions: l } })} />
            </div>

            <div className="space-y-2 pt-2 border-t border-border-subtle">
              <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider">Results timeline</p>
              {[0, 1, 2].map((i) => (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-[140px_1fr_2fr] gap-2">
                  {(['period', 'title', 'text'] as const).map((f) => (
                    <Input
                      key={f}
                      value={results[i]?.[f] ?? ''}
                      placeholder={f === 'period' ? ['3 days', '2 weeks', '4–6 weeks'][i] : f === 'title' ? 'Headline' : 'What changes'}
                      onChange={(e) => {
                        const next = [0, 1, 2].map((k) => ({ period: results[k]?.period ?? '', title: results[k]?.title ?? '', text: results[k]?.text ?? '' }));
                        next[i] = { ...next[i], [f]: e.target.value };
                        set({ results: next });
                      }}
                    />
                  ))}
                </div>
              ))}
              <Field label="Note under the timeline">
                <Input
                  value={value.resultsNote ?? ''}
                  onChange={(e) => set({ resultsNote: e.target.value })}
                  placeholder="With daily use, morning and night, and sunscreen. Individual results vary."
                />
              </Field>
            </div>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader title="New design — Why use it" subtitle="Skin concerns with a photo each. Leave empty to hide the section." />
        <CardBody className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Section intro">
              <Input value={value.whyLead ?? ''} onChange={(e) => set({ whyLead: e.target.value })} />
            </Field>
            <Field label='"How it helps" label'>
              <Input value={value.helps ?? ''} onChange={(e) => set({ helps: e.target.value })} placeholder="How the Booster helps" />
            </Field>
          </div>
          {concerns.map((c, i) => (
            <div key={i} className="border border-border-subtle rounded-lg p-3 flex gap-3">
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <ImagePick label={`Concern ${i + 1} photo`} value={c.image} onChange={(image) => setConcern(i, { image })} />
                <Field label="Question">
                  <Input value={c.question} onChange={(e) => setConcern(i, { question: e.target.value })} />
                </Field>
                <Textarea value={c.why} onChange={(e) => setConcern(i, { why: e.target.value })} placeholder="Why it happens" rows={2} />
                <Textarea value={c.how} onChange={(e) => setConcern(i, { how: e.target.value })} placeholder="How it helps" rows={2} />
                <div className="sm:col-span-2">
                  <Textarea value={c.result} onChange={(e) => setConcern(i, { result: e.target.value })} placeholder="What you'll notice" rows={2} />
                </div>
              </div>
              <button type="button" className={del} aria-label="Remove" onClick={() => set({ concerns: concerns.filter((_, j) => j !== i) })}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <Button type="button" size="sm" variant="secondary" onClick={() => set({ concerns: [...concerns, { image: '', question: '', why: '', how: '', result: '' }] })}>
            <Plus className="w-3.5 h-3.5" /> Add concern
          </Button>
          <Field label='"Sounds like your skin?" line'>
            <Input value={value.cta ?? ''} onChange={(e) => set({ cta: e.target.value })} placeholder="Start with step 01, morning and night." />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <ImagePick label="Before photo (real customer only)" value={ba.before} onChange={(before) => set({ beforeAfter: { ...ba, before, sample: false } })} />
            <ImagePick label="After photo (real customer only)" value={ba.after} onChange={(after) => set({ beforeAfter: { ...ba, after, sample: false } })} />
            <Field label="After label">
              <Input value={ba.afterLabel ?? ''} onChange={(e) => set({ beforeAfter: { ...ba, afterLabel: e.target.value } })} placeholder="After · 4 weeks" />
            </Field>
          </div>
          {(ba.before || ba.after) && (
            <Button type="button" size="sm" variant="secondary" onClick={() => set({ beforeAfter: {} })}>
              <Trash2 className="w-3.5 h-3.5" /> Remove before / after
            </Button>
          )}
          <p className="text-xs text-text-tertiary">
            The before / after slider shows only when both photos are set.
            {ba.sample && ' It now shows the draft sample image, labelled "Sample image · not a real result"; upload real customer photos to replace it.'}
          </p>
          {!!value.sampleReviews?.length && (
            <div className="flex items-center justify-between gap-3 rounded-lg bg-surface-muted p-3">
              <p className="text-xs text-text-secondary">
                {value.sampleReviews.length} draft sample reviews: shown only to staff in Preview mode, and only until real reviews are approved. Customers never see them.
              </p>
              <Button type="button" size="sm" variant="secondary" onClick={() => set({ sampleReviews: [] })}>
                <Trash2 className="w-3.5 h-3.5" /> Remove
              </Button>
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="New design — skin types and ingredients" subtitle="Empty lists use the product's skin guide and ingredient details." />
        <CardBody className="space-y-3">
          {skins.map((x, i) => (
            <div key={i} className="border border-border-subtle rounded-lg p-3 flex gap-3">
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-2">
                <ImagePick label={`Skin type ${i + 1} photo`} value={x.image} onChange={(image) => setSkin(i, { image })} />
                <Field label="Label (new line = line break)">
                  <Textarea value={x.label} onChange={(e) => setSkin(i, { label: e.target.value })} rows={2} />
                </Field>
                <Field label="Tip">
                  <Textarea value={x.tip} onChange={(e) => setSkin(i, { tip: e.target.value })} rows={2} />
                </Field>
              </div>
              <button type="button" className={del} aria-label="Remove" onClick={() => set({ skins: skins.filter((_, j) => j !== i) })}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <Button type="button" size="sm" variant="secondary" onClick={() => set({ skins: [...skins, { image: '', label: '', tip: '' }] })}>
            <Plus className="w-3.5 h-3.5" /> Add skin type
          </Button>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
            <Field label="Ingredients heading — first line">
              <Input value={ingredients.title ?? ''} onChange={(e) => set({ ingredients: { ...ingredients, title: e.target.value } })} placeholder="Four ingredients," />
            </Field>
            <Field label="Ingredients heading — second line">
              <Input value={ingredients.titleEm ?? ''} onChange={(e) => set({ ingredients: { ...ingredients, titleEm: e.target.value } })} placeholder="one light layer." />
            </Field>
          </div>
          {ings.map((x, i) => (
            <div key={i} className="border border-border-subtle rounded-lg p-3 flex gap-3">
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Input value={x.name} onChange={(e) => setIng(i, { name: e.target.value })} placeholder="Niacinamide" />
                <Input value={x.tag} onChange={(e) => setIng(i, { tag: e.target.value })} placeholder="2% · Brighten" />
                <Textarea value={x.text} onChange={(e) => setIng(i, { text: e.target.value })} placeholder="What it does" rows={2} />
                <Input value={x.inci} onChange={(e) => setIng(i, { inci: e.target.value })} placeholder="INCI name" />
              </div>
              <button type="button" className={del} aria-label="Remove" onClick={() => set({ ingredients: { ...ingredients, items: ings.filter((_, j) => j !== i) } })}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <Button type="button" size="sm" variant="secondary" onClick={() => set({ ingredients: { ...ingredients, items: [...ings, { tag: '', name: '', text: '', inci: '' }] } })}>
            <Plus className="w-3.5 h-3.5" /> Add ingredient
          </Button>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="New design — Ritual" subtitle="Step-by-step routine with a photo. No steps = hidden." />
        <CardBody className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Small label">
              <Input value={ritual.kicker ?? ''} onChange={(e) => set({ ritual: { ...ritual, kicker: e.target.value } })} placeholder="The 30-second ritual" />
            </Field>
            <Field label="Heading — first line">
              <Input value={ritual.title ?? ''} onChange={(e) => set({ ritual: { ...ritual, title: e.target.value } })} />
            </Field>
            <Field label="Heading — second line">
              <Input value={ritual.titleEm ?? ''} onChange={(e) => set({ ritual: { ...ritual, titleEm: e.target.value } })} />
            </Field>
            <Field label="Morning note">
              <Input value={ritual.am ?? ''} onChange={(e) => set({ ritual: { ...ritual, am: e.target.value } })} />
            </Field>
            <Field label="Night note">
              <Input value={ritual.pm ?? ''} onChange={(e) => set({ ritual: { ...ritual, pm: e.target.value } })} />
            </Field>
            <ImagePick label="Photo" value={ritual.image} onChange={(image) => set({ ritual: { ...ritual, image } })} />
            <Field label="Photo label — title">
              <Input value={ritual.glassTitle ?? ''} onChange={(e) => set({ ritual: { ...ritual, glassTitle: e.target.value } })} placeholder="Step 01" />
            </Field>
            <Field label="Photo label — text">
              <Input value={ritual.glassText ?? ''} onChange={(e) => set({ ritual: { ...ritual, glassText: e.target.value } })} placeholder="Water-light · 30 seconds" />
            </Field>
          </div>
          {steps.map((s, i) => (
            <div key={i} className="border border-border-subtle rounded-lg p-3 flex gap-3">
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Input value={s.time} onChange={(e) => setStep(i, { time: e.target.value })} placeholder="0s · Step A" />
                <Input value={s.title} onChange={(e) => setStep(i, { title: e.target.value })} placeholder="Step title" />
                <Input value={s.text} onChange={(e) => setStep(i, { text: e.target.value })} placeholder="Step text" />
              </div>
              <button type="button" className={del} aria-label="Remove" onClick={() => set({ ritual: { ...ritual, steps: steps.filter((_, j) => j !== i) } })}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <Button type="button" size="sm" variant="secondary" onClick={() => set({ ritual: { ...ritual, steps: [...steps, { time: '', title: '', text: '' }] } })}>
            <Plus className="w-3.5 h-3.5" /> Add step
          </Button>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="New design — Texture and video" />
        <CardBody className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Texture heading — first line">
              <Input value={texture.title ?? ''} onChange={(e) => set({ texture: { ...texture, title: e.target.value } })} placeholder="Water-light," />
            </Field>
            <Field label="Texture heading — second line">
              <Input value={texture.titleEm ?? ''} onChange={(e) => set({ texture: { ...texture, titleEm: e.target.value } })} placeholder="never sticky." />
            </Field>
            <Field label="Texture meter (0 light, 1 silky, 2 rich)">
              <Input
                type="number"
                min={0}
                max={2}
                value={String(texture.level ?? 0)}
                onChange={(e) => set({ texture: { ...texture, level: Math.max(0, Math.min(2, Number(e.target.value) || 0)) } })}
              />
            </Field>
          </div>
          <Field label="Texture text">
            <Textarea value={texture.text ?? ''} onChange={(e) => set({ texture: { ...texture, text: e.target.value } })} rows={2} />
          </Field>
          <Field label="Texture photo, YouTube or .mp4 link (blank = main product photo)">
            <Input value={texture.media ?? ''} onChange={(e) => set({ texture: { ...texture, media: e.target.value } })} />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="How-to-use clip: YouTube or .mp4 link (blank hides the video section)">
              <Input value={watch.clip ?? ''} onChange={(e) => set({ watch: { ...watch, clip: e.target.value } })} />
            </Field>
            <Field label="Full routine video: YouTube or .mp4 link (blank = the built-in routine film)">
              <Input value={watch.full ?? ''} onChange={(e) => set({ watch: { ...watch, full: e.target.value } })} placeholder="https://youtu.be/…" />
            </Field>
            <Field label="Clip poster image link (optional for YouTube)">
              <Input value={watch.poster ?? ''} onChange={(e) => set({ watch: { ...watch, poster: e.target.value } })} />
            </Field>
            <Field label="Clip label">
              <Input value={watch.label ?? ''} onChange={(e) => set({ watch: { ...watch, label: e.target.value } })} placeholder="Step 01 · Booster" />
            </Field>
            <Field label="Start of this step in the full routine video (seconds)">
              <Input type="number" min={0} value={String(watch.start ?? 0)} onChange={(e) => set({ watch: { ...watch, start: Number(e.target.value) || 0 } })} />
            </Field>
          </div>
          <Field label="Video text">
            <Textarea value={watch.line ?? ''} onChange={(e) => set({ watch: { ...watch, line: e.target.value } })} rows={2} />
          </Field>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="New design — 3-step heading and FAQs" subtitle="An empty FAQ list uses the product's normal FAQs." />
        <CardBody className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="3-step heading — first line">
              <Input value={value.routineTitle ?? ''} onChange={(e) => set({ routineTitle: e.target.value })} placeholder="Step 01 works best" />
            </Field>
            <Field label="3-step heading — second line">
              <Input value={value.routineTitleEm ?? ''} onChange={(e) => set({ routineTitleEm: e.target.value })} placeholder="before step 02." />
            </Field>
            <Field label="Line under the FAQ heading">
              <Input value={value.faqLine ?? ''} onChange={(e) => set({ faqLine: e.target.value })} placeholder="Still unsure? Our team in Dhaka replies on WhatsApp." />
            </Field>
          </div>
          {faqs.map((f, i) => (
            <div key={i} className="border border-border-subtle rounded-lg p-3 flex gap-3">
              <div className="flex-1 grid gap-2">
                <Input value={f.question} onChange={(e) => setFaq(i, { question: e.target.value })} placeholder="Question" />
                <Textarea value={f.answer} onChange={(e) => setFaq(i, { answer: e.target.value })} placeholder="Answer" rows={2} />
              </div>
              <button type="button" className={del} aria-label="Remove" onClick={() => set({ faqs: faqs.filter((_, j) => j !== i) })}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <Button type="button" size="sm" variant="secondary" onClick={() => set({ faqs: [...faqs, { question: '', answer: '' }] })}>
            <Plus className="w-3.5 h-3.5" /> Add question
          </Button>
        </CardBody>
      </Card>
    </>
  );
}
